import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Grid, Stack } from '@mui/material';

import PKDCalendar from 'components/PKDCalendar';
import { SelectInput, menuProps } from 'components/Input/SelectInput.styles';
import PKDToggleSwitch from 'components/PKDSwitch';
import {
  colorMapping,
  impactTypeOptions,
  keyMapping,
  labelMapping,
} from 'components/Dashboard/PersonalImpactCalendar/util';
import Legend from 'components/Dashboard/PersonalImpactCalendar/Legend';
import { useGetDailySurveysQuery } from 'store/api/pkd/endpoints/dailySurvey';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import {
  CheckboxLabel,
  Text,
  CategoryHeader,
  CustomCategoryHeader,
} from './styles';
import { Title } from 'components/AddCareCenter';
import { getDailySurveyCalendars, getHighlightedDays } from './utils';
import { getCustomCalendars, mockPeriodOptions } from './mock';
import { useUpsertPersonAttributeMutation } from 'store/api/pkd/endpoints/person';
import { openSnackbar } from 'store/application';
import {
  getAttributesPayload,
  getPersonAttribute,
  getPersonMedicalRecords,
} from 'pages/MedicalInformation/utils';
import PersonInformationModal from 'components/MyAccount/PersonalInformation/Modal';
import DefaultWrapper from 'shared-components/DefaultWrapper';
import { StyedMenuItem } from 'shared-components/Input/styles';
import { TaskStatus } from 'utils/enum';

const PersonalCalendar = () => {
  const dispatch = useDispatch();
  const {
    person,
    isPersonLoading,
    personId,
    auth0User,
    personAccountProfileId,
    isPersonAttributesFetching,
  } = useGetPersonAndAttributes();

  const { data: dailySurveys = [], isLoading: isDailySurveyLoading } =
    useGetDailySurveysQuery(
      {
        personAccountProfileId,
        params : {
          status  : TaskStatus.Completed,
          include : 'last-participant-task-entry',
        },
      },
      {
        skip             : !personAccountProfileId,
        selectFromResult : ({ data, isLoading }) => ({
          data : data?.map((item) => item.data),
          isLoading,
        }),
      }
    );

  const [upsertPersonAttribute, { isLoading: isPersonAttributeUpserting }] =
    useUpsertPersonAttributeMutation();

  // States and refs
  const [impactType, setImpactType] = useState('howBotheredFatigue');
  const [periodType, setPeriodType] = useState('Last_4_months');
  const [startPeriod, setStartPeriod] = useState(null);
  const [endPeriod, setEndPeriod] = useState(null);
  const [customPeriodError, setCustomPeriodError] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const hasOptedInRef = useRef(false);

  const personInfo = {
    ...getPersonMedicalRecords(person?.attributes, {
      pulseSurveyOptIn : '',
      phone            : '',
    }),
  };

  const enabled = Boolean(personInfo?.pulseSurveyOptIn === 'yes');

  const handleImpactTypeChange = (event) => setImpactType(event.target.value);
  const handleStartPeriodChange = (event) => setStartPeriod(event.target.value);
  const handleEndPeriodChange = (event) => setEndPeriod(event.target.value);

  const handlePeriodChange = (event) => setPeriodType(event.target.value);

  const handleSavePulseSurveyOptIn = async () => {
    // Call API to opt-in or out
    const attributes = getPersonAttribute(person?.attributes, personInfo);
    const body = getAttributesPayload(
      { pulseSurveyOptIn: enabled ? 'no' : 'yes' },
      attributes
    );
    if (body.length > 0) {
      // upsert attributes
      const { error } = await upsertPersonAttribute({
        body,
        personId,
      });
      if (error) {
        return dispatch(
          openSnackbar({
            type    : 'error',
            message : 'Could not complete request. Please try again',
          })
        );
      }
    }

    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Pulse survey request successfully saved',
      })
    );
    setIsOpenModal(false);
  };

  const handleEnable = async () => {
    if (!personInfo?.phone && !enabled) {
      return setIsOpenModal(true);
    }
    handleSavePulseSurveyOptIn();
  };

  useEffect(() => {
    const start = new Date(startPeriod);
    const end = new Date(endPeriod);
    setCustomPeriodError(null);
    if (start > end && startPeriod && endPeriod) {
      setCustomPeriodError('End date should not be less than start date');
    }
  }, [startPeriod, endPeriod]);

  // TODO: replace this mock calendar data with real data from backend
  // const calendars = getCalendars(periodType, startPeriod, endPeriod);
  const customCalendars = getCustomCalendars();
  const periodOptions = mockPeriodOptions;
  const calendars = getDailySurveyCalendars(
    dailySurveys,
    periodType,
    startPeriod,
    endPeriod
  );

  const isCalendarLoading =
    isPersonAttributeUpserting ||
    isPersonLoading ||
    isPersonAttributesFetching ||
    isDailySurveyLoading;

  return (
    <DefaultWrapper
      crumbs={[{ title: 'Personal impact calendar', isActive: true }]}>
      <Box
        sx={{
          display        : 'flex',
          justifyContent : 'space-between',
          flexDirection  : { xs: 'column', lg: 'row' },
        }}>
        <Title>Personal Impact Calendar</Title>
        <Box
          sx={{ display: 'flex', marginTop: '10px', alignItems: 'center' }}>
          <CheckboxLabel>Receive weekly pulse-survey via sms</CheckboxLabel>
          <Box
            sx={{
              margin     : '2px 0px 0px 11px',
              display    : 'flex',
              alignItems : 'center',
            }}>
            <PKDToggleSwitch
              onChange={handleEnable}
              checked={enabled}
              switcher={enabled ? 'on' : 'off'}
              disabled={isCalendarLoading}
            />
            {isCalendarLoading && (
              <CircularProgress
                sx={{ marginLeft: 1 }}
                color='inherit'
                size={15}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Text>
        Keep track of how your pain and the fatigue impacts on your life over
        the calendar year. <br />
        <br />
        By answering these questions each time you log-in, we&apos;ll be able to fill
        in the heat map to the right and organize your responses into a report.
        <br />
        <br /> We hope this will help inform conversations with your doctor.
      </Text>
      <Box
        sx={{
          display       : 'flex',
          marginTop     : '40px',
          flexDirection : { xs: 'column', md: 'row' },
          gap           : '1rem',
        }}>
        <Stack
          sx={{ width: { xs: '100%', md: '50%' } }}>
          <CategoryHeader
            sx={{ minWidth: { xs: '100%', md: '350px' } }}>
            Pulse Survey Questions
          </CategoryHeader>
          <Box
            sx={{ marginTop: '10px', maxWidth: { xs: '100%', lg: '310px' } }}>
            <SelectInput
              label='Select'
              menuProps={menuProps}
              value={impactType}
              onChange={handleImpactTypeChange}
              disabled={isCalendarLoading}>
              {impactTypeOptions.map((option) => (
                <StyedMenuItem
                  key={option.id} value={option.value}>
                  {option.label}
                </StyedMenuItem>
              ))}
            </SelectInput>
          </Box>
          <CategoryHeader
            sx={{ marginTop: '28px', minWidth: { xs: '100%', md: '350px' } }}>
            Period
          </CategoryHeader>
          <Box
            sx={{ marginTop: '10px', maxWidth: { xs: '100%', lg: '310px' } }}>
            <SelectInput
              label='Select'
              menuProps={menuProps}
              value={periodType}
              onChange={handlePeriodChange}
              disabled={isCalendarLoading}>
              {periodOptions.map((option) => (
                <StyedMenuItem
                  key={option.id} value={option.value}>
                  {option.label}
                </StyedMenuItem>
              ))}
            </SelectInput>
          </Box>
          {periodType === 'Custom' && (
            <Box
              sx={{ display: 'flex', maxWidth: '350px' }}>
              <Box
                sx={{ display: 'flex', flexDirection: 'column' }}>
                <CustomCategoryHeader>Start date</CustomCategoryHeader>
                <Box
                  sx={{ maxWidth: '134px', marginTop: '5px' }}>
                  <SelectInput
                    label='Select'
                    menuProps={menuProps}
                    value={startPeriod}
                    onChange={handleStartPeriodChange}>
                    {customCalendars.map((option) => (
                      <StyedMenuItem
                        key={option.id} value={option.id}>
                        {option.value}
                      </StyedMenuItem>
                    ))}
                  </SelectInput>
                </Box>
              </Box>
              <Box
                sx={{ display: 'flex', flexDirection: 'column' }}>
                <CustomCategoryHeader>End date</CustomCategoryHeader>
                <Box
                  sx={{ maxWidth: '134px', marginTop: '5px' }}>
                  <SelectInput
                    label='Select'
                    menuProps={menuProps}
                    value={endPeriod}
                    onChange={handleEndPeriodChange}>
                    {customCalendars.map((option) => (
                      <StyedMenuItem
                        key={option.id} value={option.id}>
                        {option.value}
                      </StyedMenuItem>
                    ))}
                  </SelectInput>
                </Box>
              </Box>
            </Box>
          )}
          <Text
            mt={2}>{customPeriodError}</Text>
          <Box
            sx={{ marginTop: '40px' }}>
            <Legend
              labelMapping={labelMapping[impactType]}
              colorMapping={colorMapping[impactType]}
              inline={false}
            />
          </Box>
        </Stack>
        <Grid
          container spacing={2} sx={{ justifyContent: 'center' }}>
          {isCalendarLoading
            ? (
              <CircularProgress
                sx={{ marginLeft: 1 }}
                color='inherit'
                size={15}
              />
            )
            : (
              <>
                {calendars?.map((calendar) => (
                  <Grid
                    key={calendar.date}
                    item
                    xs={12}
                    md={6}
                    sx={{ maxWidth: '600px' }}>
                    <PKDCalendar
                      month={calendar.month}
                      year={calendar.year}
                      impactType={impactType}
                      highlightedDays={getHighlightedDays(calendar.surveys)}
                      colorMapping={colorMapping}
                      keyMapping={keyMapping}
                    />
                  </Grid>
                ))}
              </>
            )}
        </Grid>
      </Box>
      {isOpenModal && (
        <PersonInformationModal
          isOpenModal={isOpenModal}
          handleClose={() => setIsOpenModal(false)}
          person={person}
          auth0User={auth0User}
          personId={personId}
          isPulseSurveyOpenModal={true}
          isPersonLoading={isPersonLoading}
          hasOptedInRef={hasOptedInRef}
          handleIsOptingIn={handleSavePulseSurveyOptIn}
        />
      )}
    </DefaultWrapper>
  );
};

export default PersonalCalendar;
